import React, { useCallback, useRef, useState } from 'react';
import Draggable, { type DraggableData, type DraggableEvent } from 'react-draggable';
import { Settings2, RotateCcw, X } from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Sheet, SheetContent } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { useThemeConfig } from '../../hooks/use-theme-config';
import { type LayoutConfig } from '../../hooks/use-layout-config';
import { ThemeControls } from './theme-controls';
import { LayoutControls } from './layout-controls';
import { cn } from '../../lib/utils';

interface CustomizerPanelProps {
  layoutConfig: LayoutConfig;
  onLayoutChange: (config: LayoutConfig) => void;
}

interface FabPosition {
  x: number;
  y: number;
}

const FAB_POS_KEY = 'converse_customizer_fab_pos';

const loadFabPosition = (): FabPosition => {
  try {
    const raw = localStorage.getItem(FAB_POS_KEY);
    if (!raw) return { x: 0, y: 0 };
    const parsed = JSON.parse(raw) as FabPosition;
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }
  return { x: 0, y: 0 };
};

export const CustomizerPanel: React.FC<CustomizerPanelProps> = ({
  layoutConfig,
  onLayoutChange,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<FabPosition>(loadFabPosition);
  const { themeConfig, setThemeConfig, resetTheme } = useThemeConfig();
  const nodeRef = useRef<HTMLDivElement>(null);
  const draggedRef = useRef(false);
  const dragOriginRef = useRef<FabPosition>({ x: 0, y: 0 });

  const handleReset = () => {
    resetTheme();
    onLayoutChange({ sidebarVariant: 'inset', sidebarCollapsible: 'offcanvas', sidebarSide: 'left' });
  };

  const handleStart = useCallback((_e: DraggableEvent, data: DraggableData) => {
    draggedRef.current = false;
    dragOriginRef.current = { x: data.x, y: data.y };
  }, []);

  const handleDrag = useCallback((_e: DraggableEvent, data: DraggableData) => {
    const dx = Math.abs(data.x - dragOriginRef.current.x);
    const dy = Math.abs(data.y - dragOriginRef.current.y);
    if (dx > 4 || dy > 4) draggedRef.current = true;
  }, []);

  const handleStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    const next = { x: data.x, y: data.y };
    setPosition(next);
    localStorage.setItem(FAB_POS_KEY, JSON.stringify(next));
  }, []);

  const handleFabClick = () => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setOpen(true);
  };

  const DraggableComponent = Draggable as unknown as React.ComponentType<any>;

  return (
    <>
      {/* ── Floating trigger — draggable; position persisted in localStorage ─ */}
      <TooltipProvider>
        <DraggableComponent
          nodeRef={nodeRef}
          bounds="body"
          position={position}
          onStart={handleStart}
          onDrag={handleDrag}
          onStop={handleStop}
        >
          <div
            ref={nodeRef}
            className="fixed bottom-6 right-6 z-50"
            style={{ touchAction: 'none' }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-12 w-12 rounded-full bg-foreground text-background',
                    'hover:bg-foreground/90 shadow-2xl [&_svg]:size-5 cursor-grab active:cursor-grabbing',
                  )}
                  onClick={handleFabClick}
                  aria-label="Open customizer (drag to move)"
                >
                  <Settings2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Customize · drag to move</TooltipContent>
            </Tooltip>
          </div>
        </DraggableComponent>
      </TooltipProvider>

      {/* ── Customizer Sheet ─────────────────────────────────────────── */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[380px] sm:w-[380px] p-0 flex flex-col gap-0 [&>button]:hidden dark:bg-popover"
        >
          {/* Sticky header */}
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 sticky top-0 bg-background dark:bg-popover z-10">
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-foreground">Customizer</span>
            </div>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipProvider>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleReset}
                      aria-label="Reset all settings"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Reset to defaults</TooltipContent>
                </TooltipProvider>
              </Tooltip>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setOpen(false)}
                aria-label="Close customizer"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="theme" className="flex flex-1 flex-col min-h-0">
            <div className="px-4 pt-3 pb-3 shrink-0">
              <TabsList className="grid w-full grid-cols-2 dark:bg-muted/40">
                <TabsTrigger
                  value="theme"
                  className="gap-1.5 text-sm dark:data-[state=active]:bg-foreground dark:data-[state=active]:text-background dark:data-[state=active]:shadow-none"
                >
                  <span>🎨</span>
                  Theme
                </TabsTrigger>
                <TabsTrigger
                  value="layout"
                  className="gap-1.5 text-sm dark:data-[state=active]:bg-foreground dark:data-[state=active]:text-background dark:data-[state=active]:shadow-none"
                >
                  <span>⊞</span>
                  Layout
                </TabsTrigger>
              </TabsList>
            </div>

            <ScrollArea className="flex-1">
              <TabsContent value="theme" className="mt-0 p-4 pb-8 space-y-0">
                <ThemeControls
                  config={themeConfig}
                  onChange={(update) => setThemeConfig(update)}
                />
              </TabsContent>
              <TabsContent value="layout" className="mt-0 p-4 pb-8 space-y-0">
                <LayoutControls config={layoutConfig} onChange={onLayoutChange} />
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
};
